import React, { useEffect, useMemo, useState } from 'react';
import {
  MapPin,
  Package,
  Radio,
  RefreshCw,
  RotateCcw,
  Save,
  Truck
} from 'lucide-react';
import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore/lite';
import { db, ensureFirebaseAuth } from '../../firebase';
import { apiClient } from '../../api';
import {
  DEFAULT_DELIVERY_CONFIG,
  getStoredDeliveryConfig,
  normalizeDeliveryConfig,
  saveStoredDeliveryConfig
} from '../../utils/deliveryConfig';
import './AdminDeliveryRadius.css';

const PRIMARY_SETTINGS_COLLECTION = 'adminSettings';
const PRIMARY_SETTINGS_DOCUMENT = 'deliveryRadius';
const FALLBACK_SETTINGS_COLLECTION = 'adminCoupons';
const FALLBACK_SETTINGS_DOCUMENT = '_meta_deliveryRadiusConfig';
const FIRESTORE_TIMEOUT_MS = 12000;

const withTimeout = (promise, timeoutMessage) => (
  Promise.race([
    promise,
    new Promise((_, reject) => {
      window.setTimeout(() => reject(new Error(timeoutMessage)), FIRESTORE_TIMEOUT_MS);
    })
  ])
);

const mapConfigToFormState = (config) => ({
  products: {
    visibilityRadiusKm: String(config.products.visibilityRadiusKm),
    featuredProductsLimit: String(config.products.featuredProductsLimit),
    freeDeliveryThreshold: String(config.products.freeDeliveryThreshold),
    deliveryCharge: String(config.products.deliveryCharge)
  },
  packages: {
    packageTypes: config.packages.packageTypes.join(', '),
    baseFee: String(config.packages.baseFee),
    perKmFee: String(config.packages.perKmFee),
    maxDistanceKm: String(config.packages.maxDistanceKm),
    mapRadiusMeters: String(config.packages.mapRadiusMeters)
  },
  deliveryPartners: {
    orderRadiusKm: String(config?.deliveryPartners?.orderRadiusKm ?? 3.0),
    packageRadiusKm: String(config?.deliveryPartners?.packageRadiusKm ?? 5.0)
  }
});

const mapFormToConfig = (formState) => normalizeDeliveryConfig({
  products: {
    visibilityRadiusKm: formState.products.visibilityRadiusKm,
    featuredProductsLimit: formState.products.featuredProductsLimit,
    freeDeliveryThreshold: formState.products.freeDeliveryThreshold,
    deliveryCharge: formState.products.deliveryCharge
  },
  packages: {
    packageTypes: formState.packages.packageTypes,
    baseFee: formState.packages.baseFee,
    perKmFee: formState.packages.perKmFee,
    maxDistanceKm: formState.packages.maxDistanceKm,
    mapRadiusMeters: formState.packages.mapRadiusMeters
  },
  deliveryPartners: {
    orderRadiusKm: formState?.deliveryPartners?.orderRadiusKm,
    packageRadiusKm: formState?.deliveryPartners?.packageRadiusKm
  }
});

const SETTINGS_LOCATIONS = [
  {
    key: 'primary',
    label: `${PRIMARY_SETTINGS_COLLECTION}/${PRIMARY_SETTINGS_DOCUMENT}`,
    ref: () => doc(db, PRIMARY_SETTINGS_COLLECTION, PRIMARY_SETTINGS_DOCUMENT)
  },
  {
    key: 'fallback',
    label: `${FALLBACK_SETTINGS_COLLECTION}/${FALLBACK_SETTINGS_DOCUMENT}`,
    ref: () => doc(db, FALLBACK_SETTINGS_COLLECTION, FALLBACK_SETTINGS_DOCUMENT)
  }
];

const isPermissionDeniedError = (error) => (
  error?.code === 'permission-denied'
  || String(error?.message || '').toLowerCase().includes('permission-denied')
);

const isDeliveryConfigDocument = (data) => (
  Boolean(
    data
    && typeof data === 'object'
    && data.products
    && data.packages
  )
);

const buildConfigPayload = (config, includeCreatedAt = false) => ({
  entityType: 'deliveryRadiusConfig',
  ...config,
  ...(includeCreatedAt ? { createdAt: serverTimestamp() } : {}),
  updatedAt: serverTimestamp()
});

const formatFirebaseError = (error, fallbackMessage) => {
  const errorCode = error?.code ? ` (${error.code})` : '';
  const errorMessage = error?.message || fallbackMessage;
  return `${fallbackMessage}${errorCode}: ${errorMessage}`;
};

const AdminDeliveryRadius = () => {
  const [formState, setFormState] = useState(mapConfigToFormState(DEFAULT_DELIVERY_CONFIG));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncError, setSyncError] = useState('');
  const [syncMessage, setSyncMessage] = useState('');
  const [lastSyncedAt, setLastSyncedAt] = useState('');
  const [storageLocation, setStorageLocation] = useState('');

  let backendSettings = null;

  const applyConfig = (config, apiPartnerSettings = null) => {
    const normalized = normalizeDeliveryConfig(config);
    if (apiPartnerSettings) {
      normalized.deliveryPartners = {
        orderRadiusKm: apiPartnerSettings.orderRadiusKm ?? normalized.deliveryPartners.orderRadiusKm,
        packageRadiusKm: apiPartnerSettings.packageRadiusKm ?? normalized.deliveryPartners.packageRadiusKm
      };
    }
    setFormState(mapConfigToFormState(normalized));
    saveStoredDeliveryConfig(normalized);
    return normalized;
  };

  const loadSettings = async () => {
    setLoading(true);
    setSyncError('');

    try {
      // 1. Fetch live partner radius settings from backend API
      try {
        const apiRes = await apiClient.get('/api/admin/settings/delivery-radius');
        if (apiRes?.data?.success && apiRes?.data?.settings) {
          backendSettings = apiRes.data.settings;
        }
      } catch (apiErr) {
        console.warn('Backend API radius load error (will use fallback):', apiErr.message);
      }

      await ensureFirebaseAuth();

      const localFallbackConfig = normalizeDeliveryConfig(getStoredDeliveryConfig());
      let lastPermissionError = null;

      for (const location of SETTINGS_LOCATIONS) {
        try {
          const snapshot = await withTimeout(
            getDoc(location.ref()),
            `Timed out while loading delivery radius settings from Firestore (${location.label}).`
          );

          if (!snapshot.exists()) {
            const payload = buildConfigPayload(localFallbackConfig, true);

            await withTimeout(
              setDoc(location.ref(), payload, { merge: true }),
              `Timed out while seeding delivery radius settings in Firestore (${location.label}).`
            );

            applyConfig(localFallbackConfig, backendSettings);
            setStorageLocation(location.label);
            setSyncMessage(
              location.key === 'fallback'
                ? 'Primary Firestore path was blocked. Settings were stored in Firebase fallback document.'
                : 'Default delivery settings were seeded to Firebase.'
            );
            setLastSyncedAt(new Date().toLocaleString());
            return;
          }

          const data = snapshot.data() || {};
          if (!isDeliveryConfigDocument(data)) {
            continue;
          }

          applyConfig(data, backendSettings);
          setStorageLocation(location.label);
          setLastSyncedAt(data?.updatedAt?.toDate ? data.updatedAt.toDate().toLocaleString() : 'Synced');

          if (location.key === 'fallback') {
            setSyncMessage('Using Firebase fallback document because the primary settings path is restricted.');
          }
          return;
        } catch (error) {
          if (isPermissionDeniedError(error)) {
            lastPermissionError = error;
            continue;
          }
          throw error;
        }
      }

      if (lastPermissionError) {
        throw lastPermissionError;
      }

      applyConfig(localFallbackConfig, backendSettings);
      setStorageLocation('local browser storage');
      setSyncMessage('No Firebase document found. Using local browser storage for now.');
    } catch (error) {
      console.error('Failed to load delivery radius settings from Firebase', error);
      applyConfig(getStoredDeliveryConfig(), backendSettings);
      setStorageLocation('local browser storage');
      setSyncError(formatFirebaseError(
        error,
        'Could not load delivery radius settings from Firestore'
      ));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  useEffect(() => {
    if (!syncMessage) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => setSyncMessage(''), 4000);
    return () => window.clearTimeout(timeoutId);
  }, [syncMessage]);

  const handleFieldChange = (section, key, value) => {
    setFormState((current) => ({
      ...current,
      [section]: {
        ...current[section],
        [key]: value
      }
    }));
  };

  const handleResetDefaults = () => {
    setFormState(mapConfigToFormState(DEFAULT_DELIVERY_CONFIG));
    setSyncMessage('Defaults restored. Save to sync them to Database & Firebase.');
    setSyncError('');
  };

  const handleSave = async () => {
    setSaving(true);
    setSyncError('');

    try {
      // 1. Save to Backend API Database
      let backendSuccess = false;
      try {
        const payload = {
          orderRadiusKm: parseFloat(formState.deliveryPartners.orderRadiusKm) || 3.0,
          packageRadiusKm: parseFloat(formState.deliveryPartners.packageRadiusKm) || 5.0
        };
        const apiRes = await apiClient.put('/api/admin/settings/delivery-radius', payload);
        if (apiRes?.data?.success) {
          backendSuccess = true;
        }
      } catch (apiErr) {
        console.warn('Backend radius update warning:', apiErr.message);
      }

      await ensureFirebaseAuth();

      const normalizedConfig = mapFormToConfig(formState);
      let saved = false;
      let lastPermissionError = null;

      for (const location of SETTINGS_LOCATIONS) {
        try {
          await withTimeout(
            setDoc(location.ref(), buildConfigPayload(normalizedConfig), { merge: true }),
            `Timed out while saving delivery radius settings to Firestore (${location.label}).`
          );
          setStorageLocation(location.label);
          setSyncMessage(
            backendSuccess
              ? 'Delivery radius settings saved to database & synced to Firebase!'
              : 'Delivery radius settings synced to Firebase.'
          );
          saved = true;
          break;
        } catch (error) {
          if (isPermissionDeniedError(error)) {
            lastPermissionError = error;
            continue;
          }
          throw error;
        }
      }

      if (!saved) {
        if (backendSuccess) {
          setSyncMessage('Delivery radius settings saved to database successfully!');
          saved = true;
        } else if (lastPermissionError) {
          throw lastPermissionError;
        } else {
          throw new Error('No writable Firebase location found for delivery settings.');
        }
      }

      applyConfig(normalizedConfig);
      setLastSyncedAt(new Date().toLocaleString());
    } catch (error) {
      console.error('Failed to save delivery radius settings to Firebase', error);
      saveStoredDeliveryConfig(mapFormToConfig(formState));
      setStorageLocation('local browser storage');
      setSyncError(formatFirebaseError(
        error,
        'Could not save delivery radius settings to Firestore. Local browser copy was updated instead'
      ));
    } finally {
      setSaving(false);
    }
  };

  const summary = useMemo(() => {
    const normalized = mapFormToConfig(formState);

    return {
      productRadiusKm: normalized.products.visibilityRadiusKm,
      featuredProductsLimit: normalized.products.featuredProductsLimit,
      freeDeliveryThreshold: normalized.products.freeDeliveryThreshold,
      packageTypesCount: normalized.packages.packageTypes.length,
      packageMaxDistanceKm: normalized.packages.maxDistanceKm,
      mapRadiusMeters: normalized.packages.mapRadiusMeters,
      orderPartnerRadiusKm: normalized.deliveryPartners.orderRadiusKm,
      packagePartnerRadiusKm: normalized.deliveryPartners.packageRadiusKm
    };
  }, [formState]);

  return (
    <div className="admin-delivery-radius">
      <div className="delivery-radius-header">
        <div>
          <h1>Radius & Package Settings</h1>
          <p>Product visibility and package delivery rules are synced with Firebase Firestore.</p>
        </div>

        <div className="delivery-radius-header-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={loadSettings}
            disabled={loading || saving}
          >
            <RefreshCw size={18} />
            Reload
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleResetDefaults}
            disabled={loading || saving}
          >
            <RotateCcw size={18} />
            Reset Defaults
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSave}
            disabled={loading || saving}
          >
            <Save size={18} />
            {saving ? 'Saving...' : 'Save to Firebase'}
          </button>
        </div>
      </div>

      {syncError ? <div className="delivery-radius-banner error">{syncError}</div> : null}
      {syncMessage ? <div className="delivery-radius-banner success">{syncMessage}</div> : null}

      <div className="delivery-radius-summary">
        <div className="delivery-summary-card">
          <div className="delivery-summary-icon product">
            <MapPin size={22} />
          </div>
          <div>
            <span className="delivery-summary-label">Products Radius</span>
            <strong>{summary.productRadiusKm} km</strong>
            <p>{summary.featuredProductsLimit} featured products, free delivery at Rs {summary.freeDeliveryThreshold}</p>
          </div>
        </div>

        <div className="delivery-summary-card">
          <div className="delivery-summary-icon package">
            <Package size={22} />
          </div>
          <div>
            <span className="delivery-summary-label">Package Coverage</span>
            <strong>{summary.packageMaxDistanceKm} km</strong>
            <p>{summary.packageTypesCount} package types, map radius {summary.mapRadiusMeters}m</p>
          </div>
        </div>

        <div className="delivery-summary-card">
          <div className="delivery-summary-icon sync">
            <Truck size={22} />
          </div>
          <div>
            <span className="delivery-summary-label">Firebase Sync</span>
            <strong>{loading ? 'Loading...' : 'Active'}</strong>
            <p>
              {storageLocation ? `Storage: ${storageLocation}` : 'Waiting for first sync'}
              {lastSyncedAt ? ` • Last sync: ${lastSyncedAt}` : ''}
            </p>
          </div>
        </div>
        <div className="delivery-summary-card">
          <div className="delivery-summary-icon product">
            <Radio size={22} />
          </div>
          <div>
            <span className="delivery-summary-label">Rider Alert Radius</span>
            <strong>Orders: {summary.orderPartnerRadiusKm} km • Packages: {summary.packagePartnerRadiusKm} km</strong>
            <p>Live location filter for delivery partner job alerts</p>
          </div>
        </div>
      </div>

      <div className="delivery-radius-layout">
        <section className="delivery-settings-panel">
          <div className="panel-head">
            <h2>Delivery Partner Radius Settings (Live Alerts & Jobs)</h2>
          </div>

          <div className="delivery-form-grid">
            <label className="delivery-field">
              <span>Order Delivery Partner Radius (km)</span>
              <input
                type="number"
                step="0.5"
                min="0.5"
                max="100"
                value={formState.deliveryPartners.orderRadiusKm}
                onChange={(event) => handleFieldChange('deliveryPartners', 'orderRadiusKm', event.target.value)}
                disabled={loading || saving}
              />
              <small>Delivery partner to store/vendor maximum distance for incoming order alerts. Default: 3.0 km.</small>
            </label>

            <label className="delivery-field">
              <span>Package Delivery Partner Radius (km)</span>
              <input
                type="number"
                step="0.5"
                min="0.5"
                max="100"
                value={formState.deliveryPartners.packageRadiusKm}
                onChange={(event) => handleFieldChange('deliveryPartners', 'packageRadiusKm', event.target.value)}
                disabled={loading || saving}
              />
              <small>Delivery partner to package pickup point maximum distance for incoming package alerts. Default: 5.0 km.</small>
            </label>
          </div>
        </section>

        <section className="delivery-settings-panel">
          <div className="panel-head">
            <h2>Products</h2>
          </div>

          <div className="delivery-form-grid">
            <label className="delivery-field">
              <span>Product visibility radius (km)</span>
              <input
                type="number"
                min="0"
                value={formState.products.visibilityRadiusKm}
                onChange={(event) => handleFieldChange('products', 'visibilityRadiusKm', event.target.value)}
                disabled={loading || saving}
              />
              <small>Default was 5 km.</small>
            </label>

            <label className="delivery-field">
              <span>Featured products limit</span>
              <input
                type="number"
                min="0"
                value={formState.products.featuredProductsLimit}
                onChange={(event) => handleFieldChange('products', 'featuredProductsLimit', event.target.value)}
                disabled={loading || saving}
              />
              <small>Default was 8 featured products.</small>
            </label>

            <label className="delivery-field">
              <span>Free delivery threshold</span>
              <input
                type="number"
                min="0"
                value={formState.products.freeDeliveryThreshold}
                onChange={(event) => handleFieldChange('products', 'freeDeliveryThreshold', event.target.value)}
                disabled={loading || saving}
              />
              <small>Default was Rs 200.</small>
            </label>

            <label className="delivery-field">
              <span>Delivery charge</span>
              <input
                type="number"
                min="0"
                value={formState.products.deliveryCharge}
                onChange={(event) => handleFieldChange('products', 'deliveryCharge', event.target.value)}
                disabled={loading || saving}
              />
              <small>Default was Rs 30.</small>
            </label>
          </div>
        </section>

        <section className="delivery-settings-panel">
          <div className="panel-head">
            <h2>Packages</h2>
          </div>

          <div className="delivery-form-grid">
            <label className="delivery-field delivery-field-full">
              <span>Package types list</span>
              <textarea
                rows="4"
                value={formState.packages.packageTypes}
                onChange={(event) => handleFieldChange('packages', 'packageTypes', event.target.value)}
                disabled={loading || saving}
                placeholder="Documents, Groceries, Electronics, Fragile"
              />
              <small>Comma-separated list. This is now stored in Firebase.</small>
            </label>

            <label className="delivery-field">
              <span>Base fee</span>
              <input
                type="number"
                min="0"
                value={formState.packages.baseFee}
                onChange={(event) => handleFieldChange('packages', 'baseFee', event.target.value)}
                disabled={loading || saving}
              />
              <small>Default was Rs 30.</small>
            </label>

            <label className="delivery-field">
              <span>Per-km fee</span>
              <input
                type="number"
                min="0"
                value={formState.packages.perKmFee}
                onChange={(event) => handleFieldChange('packages', 'perKmFee', event.target.value)}
                disabled={loading || saving}
              />
              <small>Default was Rs 8/km.</small>
            </label>

            <label className="delivery-field">
              <span>Max distance (km)</span>
              <input
                type="number"
                min="0"
                value={formState.packages.maxDistanceKm}
                onChange={(event) => handleFieldChange('packages', 'maxDistanceKm', event.target.value)}
                disabled={loading || saving}
              />
              <small>Default was 30 km.</small>
            </label>

            <label className="delivery-field">
              <span>Map radius (meters)</span>
              <input
                type="number"
                min="0"
                value={formState.packages.mapRadiusMeters}
                onChange={(event) => handleFieldChange('packages', 'mapRadiusMeters', event.target.value)}
                disabled={loading || saving}
              />
              <small>Default was 50000 meters.</small>
            </label>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AdminDeliveryRadius;
